import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import LeaderboardPage from './pages/LeaderboardPage';
import FAQPage from './pages/FAQPage';
import CompetitionPage from './pages/CompetitionPage';
import TalkingPointsPage from './pages/TalkingPointsPage';
import NotFoundPage from './pages/NotFoundPage';
import DivisionSelectPage from './pages/DivisionSelectPage';

// Create a React Query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 15 * 60 * 1000, // 15 minutes (formerly cacheTime)
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

// Context to store the last visited division
const DivisionContext = React.createContext<{
  lastDivision: string;
  setLastDivision: (division: string) => void;
}>({
  lastDivision: 'D1',
  setLastDivision: () => {},
});

function AppRoutes() {
  const [lastDivision, setLastDivision] = useState<string>(() => {
    return localStorage.getItem('lastDivision') || '';
  });

  useEffect(() => {
    if (lastDivision) {
      localStorage.setItem('lastDivision', lastDivision);
    }
  }, [lastDivision]);

  return (
    <DivisionContext.Provider value={{ lastDivision, setLastDivision }}>
      <Routes>
        <Route
          path="/"
          element={
            lastDivision ? (
              <Navigate to={`/leaderboard/${lastDivision}`} replace />
            ) : (
              <DivisionSelectPage />
            )
          }
        />
        <Route path="/leaderboard/:division" element={<LeaderboardPage />} />
        <Route path="/faqs" element={<FAQPage />} />
        <Route path="/competition" element={<CompetitionPage />} />
        <Route path="/talking-points" element={<TalkingPointsPage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </DivisionContext.Provider>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
    </QueryClientProvider>
  );
}

export default App;
export { DivisionContext };
